<template lang="pug">
  div
    // Loading state
    template(v-if="isFetching && !isInitialized")
      div(style="max-width: 1200px; margin: auto; padding: 40px;")
        claim-loading-state

    // Error state
    template(v-else-if="isError")
      div(style="max-width: 1200px; margin: auto; padding: 40px;")
        yz-empty-state-card(icon="$yzi-alert-triangle" title="Unable to load invoices" description="The invoice information could not be retrieved. Please try again." border-color="yz-danger-500" icon-color="yz-danger-500" action-label="Try again" @action-click="fetchInvoices")

    // Content
    template(v-else)
      .py-5.px-8.bg-neutral-300(style="min-height: calc(100dvh - 72px);")
        // Header
        bulk-claim-header(:parent-claim="parentClaim" :parent-invoices="parentInvoices" @go-back="onGoBack" @updated="onClaimUpdated")

        // Content
        v-row.mt-6(no-gutters)
          // Left column - Summary and Selected Invoices
          v-col(cols="9")
            // Summary Section
            bulk-claim-summary-card(:parent-claim="parentClaim" :parent-invoices="invoicesData" :parent-invoices-total-count="totalInvoicesCount" :parent-invoice-page-size="invoicePageSize" :is-table-loading="isFetching || isRefreshingSummary" @change-page="onChangePage" @updated="onInvoiceUpdated" @switching-to-single="onSwitchingToSingle")

            // Documents Section
            claim-documents-card.mt-6(:parent-claim="parentClaim" :is-refreshing="isRefreshingDocuments" @updated="onDocumentUpdated")

            // Payments Section
            claim-payments-card.mt-6(:parent-claim="parentClaim" :is-refreshing="isRefreshingPayments" @updated="onPaymentUpdated")

            // Remarks Section
            claim-remarks-card.mt-6(:parent-claim="parentClaim")

          // Right column - Logs
          v-col.ml-3
            claim-logs-card(:parent-claim="parentClaim")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import BulkClaimHeader from '@/components/clinic/panel/claims/bulk/BulkClaimHeader.vue'
import BulkClaimSummaryCard from '@/components/clinic/panel/claims/bulk/BulkClaimSummaryCard.vue'
import ClaimDocumentsCard from '@/components/clinic/panel/claims/overview/ClaimDocumentsCard.vue'
import ClaimPaymentsCard from '@/components/clinic/panel/claims/overview/ClaimPaymentsCard.vue'
import ClaimRemarksCard from '@/components/clinic/panel/claims/overview/ClaimRemarksCard.vue'
import ClaimLogsCard from '@/components/clinic/panel/claims/overview/ClaimLogsCard.vue'
import ClaimLoadingState from '@/components/clinic/panel/claims/ClaimLoadingState.vue'

export default {
  components: { BulkClaimHeader, BulkClaimSummaryCard, ClaimDocumentsCard, ClaimPaymentsCard, ClaimRemarksCard, ClaimLogsCard, ClaimLoadingState },
  props: {
    parentClaim: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Loading states
      isFetching: false,
      isInitialized: false,
      isRefreshingDocuments: false,
      isRefreshingPayments: false,
      isRefreshingSummary: false,
      // Error state
      isError: false,
      // Invoice data
      invoicesData: [],
      totalInvoicesCount: 0,
      // Pagination
      invoicePageSize: 5
    }
  },
  mounted() {
    this.fetchInvoices()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    parentInvoices() {
      return this.invoicesData.map(invoice => invoice.originalData)
    }
  },
  watch: {
    parentClaim: {
      handler() {
        // Reset loading state when parent claim updates
        if (this.isRefreshingDocuments) {
          this.isRefreshingDocuments = false
        }
        if (this.isRefreshingPayments) {
          this.isRefreshingPayments = false
        }
        if (this.isRefreshingSummary) {
          this.isRefreshingSummary = false
        }
      },
      deep: true
    },
    'parentClaim.invoices': {
      handler(newInvoices, oldInvoices) {
        // Refetch invoices when the invoice list changes (e.g., after deletion)
        if (oldInvoices && newInvoices && newInvoices.length !== oldInvoices.length) {
          this.fetchInvoices()
        }
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchInvoices(page = 1) {
      if (!this.parentClaim.invoices?.length) {
        return
      }

      this.isFetching = true
      this.isError = false

      try {
        // Extract invoice UIDs from parentClaim
        const invoiceUids = this.parentClaim.invoices.join(',')

        // Get panel ID from the first invoice if available
        const panelId = this.parentClaim.panel?.id

        const params = {
          uids: invoiceUids,
          page_size: this.invoicePageSize,
          page: page,
          panel: panelId
        }

        const { data } = await this.$axios.get(`/panels/${this.company.slug}/invoices`, { params })

        this.totalInvoicesCount = data.count
        // Transform and store the fetched invoice data
        if (data.count > 0) {
          this.invoicesData = data.results.map(invoice => this.sanitizeData(invoice))
        } else {
          this.isError = true
          console.error('No invoices found for the bulk claim.')
          await this.showMessage(['error', 'No invoices found for this bulk claim.'])
          this.invoicesData = []
        }
      } catch (error) {
        console.error('Error fetching invoices:', error)
        this.isError = true
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
        if (!this.isError) {
          this.isInitialized = true
        }
      }
    },
    sanitizeData(invoice) {
      // Format visit date into separate date and time objects
      let visitDate = { date: '', time: '' }
      if (invoice.visit_date) {
        try {
          const dt = DateTime.fromISO(invoice.visit_date)
          visitDate = {
            date: dt.toFormat('dd MMMM yyyy'),
            time: dt.toFormat('h:mma').toLowerCase()
          }
        } catch (error) {
          console.error('Error formatting date:', error)
          visitDate = { date: invoice.visit_date, time: '' }
        }
      }

      // Group treatments by type
      const treatmentDetails = {}
      const treatmentTypes = {
        consultation: 'Consultation',
        service: 'Service',
        procedure: 'Procedure',
        medication: 'Medication',
        investigation: 'Investigation',
        document: 'Document',
        package: 'Package'
      }

      if (invoice.treatments && Array.isArray(invoice.treatments)) {
        invoice.treatments.forEach(treatment => {
          const categoryName = treatmentTypes[treatment.type] || 'Other'
          if (!treatmentDetails[categoryName]) {
            treatmentDetails[categoryName] = []
          }
          treatmentDetails[categoryName].push(treatment.name)
        })
      }

      // Format diagnosis - join array or take first item
      let diagnosis = ''
      if (invoice.diagnosis && Array.isArray(invoice.diagnosis) && invoice.diagnosis.length > 0) {
        diagnosis = invoice.diagnosis.join(', ')
      }

      return {
        originalData: invoice,
        id: invoice.id,
        visitDate,
        invoiceNo: invoice.invoice_no?.toString() || '',
        staffDetails: {
          name: invoice.staff_name || '',
          employeeId: invoice.staff_id || '',
          relationship: invoice.relationship || ''
        },
        patientDetails: {
          name: invoice.name || '',
          id: invoice.ic || '',
          chitNo: invoice.chit_number || ''
        },
        mcDays: invoice.mc_days || 0,
        diagnosis,
        treatments: `${invoice.treatments?.length || 0} items`,
        treatmentDetails,
        total: invoice.amount
      }
    },
    onGoBack() {
      this.$emit('go-back')
    },
    onChangePage(page) {
      // Re-fetch invoices when page changes
      this.fetchInvoices(page)
    },
    onClaimUpdated() {
      this.$emit('updated', { showLoading: true })
    },
    onInvoiceUpdated() {
      // If we have exactly 2 invoices before deletion, we'll have 1 after
      // This means we need to switch to single claim view
      if (this.totalInvoicesCount === 2) {
        this.$emit('switching-to-single')
        return
      }

      this.isRefreshingSummary = true
      this.$emit('updated', 'invoices')
    },
    onDocumentUpdated() {
      this.isRefreshingDocuments = true
      this.$emit('updated', 'documents')
    },
    onPaymentUpdated() {
      this.isRefreshingPayments = true
      this.$emit('updated', 'payments')
    },
    onSwitchingToSingle() {
      // Propagate the switching-to-single event from BulkClaimSummaryCard to parent
      this.$emit('switching-to-single')
    }
  }
}
</script>

<style scoped></style>
